import { useMutation } from "@apollo/client/react";
import {
  FeedbackMessageFragment,
  RemoveFeedbackMessage,
  RemoveFeedbackMessageVariables,
} from "__generated__/types";
import classNames from "classnames";
import deleteIcon from "common/assets/font-awesome/delete.svg";
import { useAuth } from "common/auth/Auth";
import { IconButton } from "common/buttons";
import { LocalDate } from "common/components/dates/LocalDate";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { MultilineText } from "common/components/MultilineText";
import { Section } from "common/components/Section";
import { SubText } from "common/components/Typography";
import gql from "graphql-tag";
import React from "react";
import styles from "./FeedbackMessage.module.css";

export const feedbackMessageFragment = gql`
  fragment FeedbackMessageFragment on FeedbackMessage {
    id
    removedAt
    body {
      content
      isMarkdown
    }
    submittedBy {
      id
      fullName
    }
    submittedAt
  }
`;

const REMOVE_MESSAGE = gql`
  mutation RemoveFeedbackMessage($input: RemoveFeedbackMessageInput!) {
    removeFeedbackMessage(input: $input) {
      feedback {
        id
        messages {
          ...FeedbackMessageFragment
        }
      }
    }
  }
  ${feedbackMessageFragment}
`;

interface Props {
  title?: string;
  message: FeedbackMessageFragment;
  feedbackId: string;
}

export const FeedbackMessage: React.FC<React.PropsWithChildren<Props>> = ({
  title,
  message,
  feedbackId,
}) => {
  const [mutate, { error, loading }] = useMutation<
    RemoveFeedbackMessage,
    RemoveFeedbackMessageVariables
  >(REMOVE_MESSAGE);

  const me = useAuth();
  const senderIsMe = message.submittedBy?.id === me.me?.principal?.user.id;

  const canRemoveMessage = me.me?.menu.admin;

  const sender =
    message.submittedBy?.id !== null && senderIsMe
      ? "meg"
      : message.submittedBy?.fullName;

  const handleRemoveMessage = async () => {
    await mutate({
      variables: {
        input: {
          feedbackId,
          messageId: message.id,
        },
      },
    });
  };

  return (
    <Section title={title} className={styles.container}>
      {loading ? (
        <MultilineText content="Sletter melding ..." isMarkdown={true} />
      ) : message.body ? (
        <div
          className={classNames(
            senderIsMe ? styles.fromMe : styles.fromOther,
            styles.messageContainer,
          )}
        >
          <MultilineText {...message.body} />
        </div>
      ) : (
        <div className={styles.deleted}>
          <em>
            <MultilineText
              content="Denne meldingen har blitt slettet."
              isMarkdown={false}
            />
          </em>
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          alignSelf: senderIsMe ? "end" : "start" /* stylelint-disable-line */,
        }}
      >
        <SubText>
          <b className={styles.sender}>
            Sendt{sender ? ` av ${sender}` : ""}{" "}
            <LocalDate date={message.submittedAt} />
          </b>
        </SubText>
        {!message.removedAt && canRemoveMessage && (
          <IconButton
            icon={deleteIcon}
            altText="Slett melding"
            onClick={handleRemoveMessage}
          />
        )}
      </div>
      <MutationLoadingOrError loading={loading} error={error} />
    </Section>
  );
};
